import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { getValidationBoard } from '@/api/cashflows';
import Spinner from '@/components/ui/Spinner';
import { formatPeriod, generateRecentPeriods } from '@/utils/format';
import type { ValidationCell, ValidationCellStatus } from '@/types';

// ── Cell colour logic ────────────────────────────────────────────────────────
function cellBg(cell: ValidationCell | undefined): string {
  if (!cell) return 'bg-gray-200';
  const s = cell.cellStatus as ValidationCellStatus;
  if (s === 'AGREED' && cell.isBalanced && cell.cf9999IsZero) return 'bg-green-500';
  if (s === 'SUBMITTED' && (!cell.isBalanced || !cell.cf9999IsZero)) return 'bg-red-500';
  if (s === 'SUBMITTED') return 'bg-amber-400';
  if (s === 'IN_PROGRESS') return 'bg-blue-400';
  if (s === 'NOT_STARTED') return 'bg-gray-200';
  if (!cell.isBalanced || !cell.cf9999IsZero) return 'bg-red-500';
  return 'bg-gray-200';
}

function actorLabel(actor: { name: string | null; email: string } | null): string {
  if (!actor) return 'Unknown user';
  return actor.name?.trim() || actor.email;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** "Submitted"/"Agreed" column for the selected period: person over date, or a dash. */
function ActorCell({
  actor,
  at,
  show,
}: {
  actor: { name: string | null; email: string } | null | undefined;
  at: string | null | undefined;
  show: boolean;
}) {
  if (!show || !at) {
    return <td className="px-3 py-2 text-xs text-gray-300">—</td>;
  }
  return (
    <td className="px-3 py-2 whitespace-nowrap">
      <div className="text-xs font-medium text-gray-800">{actorLabel(actor ?? null)}</div>
      <div className="text-xs text-gray-500">{formatDate(at)}</div>
    </td>
  );
}

/** Tooltip: status line plus who submitted / agreed and when (MH request 30 Sep 2026). */
function cellTitle(cell: ValidationCell | undefined): string {
  const lines = [cellStatusTitle(cell)];
  if (cell?.submittedAt && (cell.status === 'SUBMITTED' || cell.status === 'AGREED')) {
    lines.push(`Submitted by ${actorLabel(cell.submittedBy)} on ${formatDate(cell.submittedAt)}`);
  }
  if (cell?.agreedAt && cell.status === 'AGREED') {
    lines.push(`Agreed by ${actorLabel(cell.agreedBy)} on ${formatDate(cell.agreedAt)}`);
  }
  return lines.join('\n');
}

function cellStatusTitle(cell: ValidationCell | undefined): string {
  if (!cell) return 'No data';
  if (cell.cellStatus === 'AGREED' && cell.isBalanced && cell.cf9999IsZero) return 'Agreed & balanced';
  if (cell.cellStatus === 'SUBMITTED' && !cell.isBalanced) return 'Submitted — not balanced';
  if (cell.cellStatus === 'SUBMITTED') return 'Submitted — pending review';
  if (cell.cellStatus === 'IN_PROGRESS') return 'In progress';
  if (cell.cellStatus === 'NOT_STARTED') return 'Not started';
  if (!cell.isBalanced) return 'Not balanced';
  if (!cell.cf9999IsZero) return 'CF9999 non-zero';
  return 'Not started';
}

export default function ValidationBoardPage() {
  const navigate = useNavigate();
  const recentPeriods = generateRecentPeriods(12);
  const [selectedPeriod, setSelectedPeriod] = useState(recentPeriods[0]);
  const [collapsedDivisions, setCollapsedDivisions] = useState<Set<string>>(new Set());

  const { data: board, isLoading, isError } = useQuery({
    queryKey: ['validation-board', selectedPeriod],
    queryFn: () => getValidationBoard(selectedPeriod),
    // Keep colours live: other users submit/agree from their own browsers, so
    // poll and refetch on focus rather than requiring a manual page refresh.
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    staleTime: 15_000,
  });

  const toggleDivision = (id: string) => {
    setCollapsedDivisions((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // All periods displayed in the board columns
  const displayPeriods = board?.periods ?? [selectedPeriod];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Validation Board</h1>
          <p className="mt-1 text-sm text-gray-500">
            Red/green status for every entity and period.
          </p>
        </div>

        {/* Period filter */}
        <select
          value={selectedPeriod}
          onChange={(e) => setSelectedPeriod(e.target.value)}
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-primary-500"
          aria-label="Filter by period"
        >
          {recentPeriods.map((p) => (
            <option key={p} value={p}>
              {formatPeriod(p)}
            </option>
          ))}
        </select>
      </div>

      {/* Summary */}
      {board && (
        <div className="flex gap-6 rounded-xl border border-gray-200 bg-white p-4 shadow-sm text-sm">
          <div>
            <span className="text-gray-500">Entities green: </span>
            <span className="font-semibold text-gray-900">
              {board.summary.greenEntities} / {board.summary.totalEntities}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Divisions green: </span>
            <span className="font-semibold text-gray-900">
              {board.summary.greenDivisions} / {board.summary.totalDivisions}
            </span>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 text-xs text-gray-600">
        <div className="flex items-center gap-1.5">
          <span className="inline-block h-3.5 w-3.5 rounded-full bg-green-500" />
          Agreed &amp; balanced
        </div>
        <div className="flex items-center gap-1.5">
          <span className="inline-block h-3.5 w-3.5 rounded-full bg-amber-400" />
          Submitted
        </div>
        <div className="flex items-center gap-1.5">
          <span className="inline-block h-3.5 w-3.5 rounded-full bg-blue-400" />
          In progress
        </div>
        <div className="flex items-center gap-1.5">
          <span className="inline-block h-3.5 w-3.5 rounded-full bg-red-500" />
          Unbalanced / CF9999 non-zero
        </div>
        <div className="flex items-center gap-1.5">
          <span className="inline-block h-3.5 w-3.5 rounded-full bg-gray-200 border border-gray-300" />
          Not started
        </div>
      </div>

      {/* Board */}
      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner size="lg" />
        </div>
      ) : isError ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
          Failed to load validation board.
        </div>
      ) : board ? (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="sticky left-0 z-10 bg-gray-50 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 min-w-[200px]">
                  Entity / Division
                </th>
                <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 whitespace-nowrap">
                  Submitted — {formatPeriod(selectedPeriod)}
                </th>
                <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 whitespace-nowrap">
                  Agreed — {formatPeriod(selectedPeriod)}
                </th>
                {displayPeriods.map((p) => (
                  <th
                    key={p}
                    className="px-2 py-3 text-center text-xs font-semibold uppercase tracking-wide text-gray-500 whitespace-nowrap"
                  >
                    {formatPeriod(p)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {board.divisions.map((divRow) => (
                <>
                  {/* Division row */}
                  <tr
                    key={`div-${divRow.division.id}`}
                    className="bg-gray-50 cursor-pointer hover:bg-gray-100"
                    onClick={() => toggleDivision(divRow.division.id)}
                  >
                    <td className="sticky left-0 bg-gray-50 px-4 py-2 font-semibold text-gray-800">
                      <div className="flex items-center gap-2">
                        <svg
                          className={clsx(
                            'h-4 w-4 text-gray-500 transition-transform',
                            !collapsedDivisions.has(divRow.division.id) && 'rotate-90',
                          )}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        <span
                          className={clsx(
                            'inline-block h-3 w-3 rounded-full',
                            divRow.isGreen
                              ? 'bg-green-500'
                              : divRow.allSubmitted
                                ? 'bg-amber-400'
                                : 'bg-red-500',
                          )}
                          title={
                            divRow.isGreen
                              ? 'All entities agreed & balanced'
                              : divRow.allSubmitted
                                ? 'All entities submitted — awaiting agreement'
                                : 'Entities pending'
                          }
                        />
                        {divRow.division.name}
                      </div>
                    </td>
                    <td className="px-3 py-2" colSpan={2} />
                    {displayPeriods.map((p) => {
                      // Division is green only when all entities are green for
                      // this period; amber when every entity is at least
                      // submitted; red otherwise.
                      const allGreen = divRow.entities.every((e) => {
                        const c = e.cells[p];
                        return c?.cellStatus === 'AGREED' && c.isBalanced && c.cf9999IsZero;
                      });
                      const allSubmitted = divRow.entities.every((e) => {
                        const s = e.cells[p]?.status;
                        return s === 'SUBMITTED' || s === 'AGREED';
                      });
                      const anyData = divRow.entities.some((e) => e.cells[p]);
                      return (
                        <td key={p} className="px-2 py-2 text-center">
                          <span
                            className={clsx(
                              'inline-block h-3.5 w-3.5 rounded-full',
                              !anyData
                                ? 'bg-gray-200'
                                : allGreen
                                  ? 'bg-green-500'
                                  : allSubmitted
                                    ? 'bg-amber-400'
                                    : 'bg-red-500',
                            )}
                            title={
                              !anyData
                                ? 'No data'
                                : allGreen
                                  ? 'All entities agreed & balanced'
                                  : allSubmitted
                                    ? 'All entities submitted — awaiting agreement'
                                    : 'Entities pending'
                            }
                          />
                        </td>
                      );
                    })}
                  </tr>

                  {/* Entity rows (collapsible) */}
                  {!collapsedDivisions.has(divRow.division.id) &&
                    divRow.entities.map((entRow) => (
                      <tr
                        key={`ent-${entRow.entity.id}`}
                        className="hover:bg-gray-50"
                      >
                        <td className="sticky left-0 bg-white px-4 py-2 pl-10 text-gray-700">
                          <button
                            type="button"
                            onClick={() =>
                              navigate(`/entities/${entRow.entity.id}/cashflow/${selectedPeriod}`)
                            }
                            title={`Open ${entRow.entity.name} — ${formatPeriod(selectedPeriod)}`}
                            className="group flex items-center text-left focus:outline-none"
                          >
                            <span className="font-medium text-primary-700 group-hover:underline group-focus-visible:underline">
                              {entRow.entity.name}
                            </span>
                            <span className="ml-2 text-xs text-gray-600">{entRow.entity.code}</span>
                          </button>
                        </td>
                        <ActorCell
                          actor={entRow.cells[selectedPeriod]?.submittedBy}
                          at={entRow.cells[selectedPeriod]?.submittedAt}
                          show={['SUBMITTED', 'AGREED'].includes(entRow.cells[selectedPeriod]?.status ?? '')}
                        />
                        <ActorCell
                          actor={entRow.cells[selectedPeriod]?.agreedBy}
                          at={entRow.cells[selectedPeriod]?.agreedAt}
                          show={entRow.cells[selectedPeriod]?.status === 'AGREED'}
                        />
                        {displayPeriods.map((p) => {
                          const cell = entRow.cells[p];
                          return (
                            <td key={p} className="px-2 py-2 text-center">
                              <button
                                onClick={() =>
                                  navigate(`/entities/${entRow.entity.id}/cashflow/${p}`)
                                }
                                title={cellTitle(cell)}
                                aria-label={`${entRow.entity.name} ${formatPeriod(p)}: ${cellTitle(cell)}`}
                                className="inline-flex items-center justify-center rounded p-0.5 hover:ring-2 hover:ring-primary-400 hover:ring-offset-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                              >
                                <span
                                  className={clsx(
                                    'inline-block h-4 w-4 rounded-full',
                                    cellBg(cell),
                                  )}
                                />
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                </>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
